import { supabase } from '../lib/supabase';

export const profitCalculationService = {
  // Get all profit calculations (admin sees all, users see their own)
  async getAllCalculations() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Não autenticado');

    const { data, error } = await supabase
      ?.from('profit_calculations')
      ?.select(`
        *,
        user:user_profiles!profit_calculations_user_id_fkey(id, email, full_name)
      `)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(calc => ({
      id: calc?.id,
      userId: calc?.user_id,
      userName: calc?.user?.full_name || calc?.user?.email,
      projectName: calc?.project_name,
      totalObra: calc?.total_obra,
      valorJaFaturado: calc?.valor_ja_faturado,
      valorAFaturar: calc?.valor_a_faturar,
      custoEmpreiteiro: calc?.custo_empreiteiro,
      equipeMensalPrestadores: calc?.equipe_mensal_prestadores,
      visitasObraTotal: calc?.visitas_obra_total,
      outrosCustosDiretos: calc?.outros_custos_diretos,
      fixosMensaisEscritorio: calc?.fixos_mensais_escritorio,
      duracaoSemanas: calc?.duracao_semanas,
      duracaoMeses: calc?.duracao_meses,
      dasPercentualEfetivo: calc?.das_percentual_efetivo,
      dasBaseCalculo: calc?.das_base_calculo,
      prolaboreMensal: calc?.prolabore_mensal,
      inssPercentualSeg: calc?.inss_percentual_seg,
      inssMensal: calc?.inss_mensal,
      irrfMensal: calc?.irrf_mensal,
      dasTotal: calc?.das_total,
      darfTotal: calc?.darf_total,
      custoDiretoTotal: calc?.custo_direto_total,
      custoEquipeTotal: calc?.custo_equipe_total,
      custoFixosTotal: calc?.custo_fixos_total,
      totalGastos: calc?.total_gastos,
      lucroLiquido: calc?.lucro_liquido,
      lucroPercent: calc?.lucro_percent,
      createdAt: calc?.created_at,
      updatedAt: calc?.updated_at
    }));
  },

  // Get single calculation by ID
  async getCalculationById(id) {
    const { data, error } = await supabase
      ?.from('profit_calculations')
      ?.select(`
        *,
        user:user_profiles!profit_calculations_user_id_fkey(id, email, full_name)
      `)
      ?.eq('id', id)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      userName: data?.user?.full_name || data?.user?.email,
      projectName: data?.project_name,
      totalObra: data?.total_obra,
      valorJaFaturado: data?.valor_ja_faturado,
      valorAFaturar: data?.valor_a_faturar,
      custoEmpreiteiro: data?.custo_empreiteiro,
      equipeMensalPrestadores: data?.equipe_mensal_prestadores,
      visitasObraTotal: data?.visitas_obra_total,
      outrosCustosDiretos: data?.outros_custos_diretos,
      fixosMensaisEscritorio: data?.fixos_mensais_escritorio,
      duracaoSemanas: data?.duracao_semanas,
      duracaoMeses: data?.duracao_meses,
      dasPercentualEfetivo: data?.das_percentual_efetivo,
      dasBaseCalculo: data?.das_base_calculo,
      prolaboreMensal: data?.prolabore_mensal,
      inssPercentualSeg: data?.inss_percentual_seg,
      inssMensal: data?.inss_mensal,
      irrfMensal: data?.irrf_mensal,
      dasTotal: data?.das_total,
      darfTotal: data?.darf_total,
      custoDiretoTotal: data?.custo_direto_total,
      custoEquipeTotal: data?.custo_equipe_total,
      custoFixosTotal: data?.custo_fixos_total,
      totalGastos: data?.total_gastos,
      lucroLiquido: data?.lucro_liquido,
      lucroPercent: data?.lucro_percent,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Calculate IRRF based on tax brackets
  async calculateIRRF(baseIrrf) {
    const { data: brackets, error } = await supabase
      ?.from('irrf_tax_brackets')
      ?.select('*')
      ?.order('ordem', { ascending: true });

    if (error) throw error;

    // Find applicable bracket
    const bracket = brackets?.find(b => baseIrrf <= b?.limite_superior);
    if (!bracket) return 0;

    const irrfCalculado = (baseIrrf * (bracket?.aliquota / 100)) - bracket?.parcela_deduzir;
    return Math.max(0, irrfCalculado);
  },

  // Get tax settings
  async getTaxSettings() {
    const { data, error } = await supabase
      ?.from('tax_settings')
      ?.select('*')
      ?.limit(1)
      ?.single();

    if (error) throw error;

    return {
      defaultDasPercentual: data?.default_das_percentual,
      defaultProlaboreMensal: data?.default_prolabore_mensal,
      defaultInssPercentual: data?.default_inss_percentual
    };
  },

  // Get IRRF tax brackets
  async getIrrfBrackets() {
    const { data, error } = await supabase
      ?.from('irrf_tax_brackets')
      ?.select('*')
      ?.order('ordem', { ascending: true });

    if (error) throw error;

    return data?.map(bracket => ({
      id: bracket?.id,
      limiteSuperior: bracket?.limite_superior,
      aliquota: bracket?.aliquota,
      parcelaDeduzir: bracket?.parcela_deduzir,
      ordem: bracket?.ordem
    }));
  },

  // Create new profit calculation
  async createCalculation(calculationData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Não autenticado');

    // Calculate IRRF
    const baseIrrf = calculationData?.prolaboreMensal - (calculationData?.prolaboreMensal * (calculationData?.inssPercentualSeg / 100));
    const irrfMensal = await this.calculateIRRF(baseIrrf);

    // Calculate all values
    const inssMensal = calculationData?.prolaboreMensal * (calculationData?.inssPercentualSeg / 100);
    const darfTotal = (inssMensal + irrfMensal) * calculationData?.duracaoMeses;
    
    const dasBase = calculationData?.dasBaseCalculo === 'valor_a_faturar' 
      ? calculationData?.valorAFaturar 
      : calculationData?.totalObra;
    const dasTotal = dasBase * (calculationData?.dasPercentualEfetivo / 100);

    const custoDiretoTotal = 
      calculationData?.custoEmpreiteiro + 
      calculationData?.visitasObraTotal + 
      calculationData?.outrosCustosDiretos;
    
    const custoEquipeTotal = calculationData?.equipeMensalPrestadores * calculationData?.duracaoMeses;
    const custoFixosTotal = calculationData?.fixosMensaisEscritorio * calculationData?.duracaoMeses;
    
    const totalGastos = custoDiretoTotal + custoEquipeTotal + custoFixosTotal + dasTotal + darfTotal;
    const lucroLiquido = calculationData?.totalObra - totalGastos;
    const lucroPercent = (lucroLiquido / calculationData?.totalObra) * 100;

    const { data, error } = await supabase
      ?.from('profit_calculations')
      ?.insert({
        user_id: user?.id,
        project_name: calculationData?.projectName,
        total_obra: calculationData?.totalObra,
        valor_ja_faturado: calculationData?.valorJaFaturado,
        valor_a_faturar: calculationData?.valorAFaturar,
        custo_empreiteiro: calculationData?.custoEmpreiteiro,
        equipe_mensal_prestadores: calculationData?.equipeMensalPrestadores,
        visitas_obra_total: calculationData?.visitasObraTotal,
        outros_custos_diretos: calculationData?.outrosCustosDiretos,
        fixos_mensais_escritorio: calculationData?.fixosMensaisEscritorio,
        duracao_semanas: calculationData?.duracaoSemanas,
        duracao_meses: calculationData?.duracaoMeses,
        das_percentual_efetivo: calculationData?.dasPercentualEfetivo,
        das_base_calculo: calculationData?.dasBaseCalculo,
        prolabore_mensal: calculationData?.prolaboreMensal,
        inss_percentual_seg: calculationData?.inssPercentualSeg,
        inss_mensal: inssMensal,
        irrf_mensal: irrfMensal,
        das_total: dasTotal,
        darf_total: darfTotal,
        custo_direto_total: custoDiretoTotal,
        custo_equipe_total: custoEquipeTotal,
        custo_fixos_total: custoFixosTotal,
        total_gastos: totalGastos,
        lucro_liquido: lucroLiquido,
        lucro_percent: lucroPercent
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  // Delete calculation
  async deleteCalculation(id) {
    const { error } = await supabase
      ?.from('profit_calculations')
      ?.delete()
      ?.eq('id', id);

    if (error) throw error;
  }
};